import { useEffect, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { Box, Spinner, Text, VStack } from "@chakra-ui/react";

const REQUIRED_GROUP = "MusicDevs";

function groupsFromUser(profile: Record<string, unknown> | undefined): string[] {
  if (!profile) return [];
  const raw = profile["cognito:groups"];
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  return String(raw).split(/[\s,]+/).filter(Boolean);
}

/**
 * Gate: triggers sign-in if not authenticated, and renders a friendly
 * "not authorized" screen if the caller isn't in the MusicDevs Workspace
 * group. The backend also enforces the group check on every request —
 * this gate is purely UX, not a security boundary.
 */
export default function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator) {
      void auth.signinRedirect();
    }
  }, [auth]);

  if (auth.isLoading || !auth.isAuthenticated) {
    return (
      <Box p={8}>
        <Spinner mr={3} />
        <Text as="span">Loading…</Text>
      </Box>
    );
  }

  const groups = groupsFromUser(auth.user?.profile as Record<string, unknown> | undefined);
  if (!groups.includes(REQUIRED_GROUP)) {
    return (
      <Box p={8}>
        <VStack align="start" spacing={2}>
          <Text fontSize="lg" fontWeight="bold">Not authorized</Text>
          <Text>This site requires membership in the <code>{REQUIRED_GROUP}</code> Google Workspace group. Ask an admin to add you.</Text>
        </VStack>
      </Box>
    );
  }

  return <>{children}</>;
}
